import React, { useState } from 'react';
import { ChevronDown as ChevronDownIcon, ChevronUp as ChevronUpIcon, BookOpenIcon, HandshakeIcon, LightbulbIcon, HeartIcon, ThumbsUpIcon, SearchIcon } from 'lucide-react';
interface MessageTemplateSelectorProps {
  onSelectTemplate: (template: string) => void;
}
export const MessageTemplateSelector: React.FC<MessageTemplateSelectorProps> = ({
  onSelectTemplate
}) => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const categories = [{
    id: 'all',
    name: 'All Templates'
  }, {
    id: 'introduction',
    name: 'Introductions'
  }, {
    id: 'follow-up',
    name: 'Follow-ups'
  }, {
    id: 'value',
    name: 'Value-based'
  }, {
    id: 'event',
    name: 'Events & Webinars'
  }];
  const templates = [{
    id: 1,
    name: 'Product Introduction',
    category: 'introduction',
    icon: <LightbulbIcon size={16} className="text-blue-600" data-id="element-644" />,
    description: 'Introduce your product/service based on their engagement',
    template: 'Hi [Name],\n\nI noticed you recently engaged with content about [Topic]. I thought you might be interested in learning how [Your Company] helps businesses with [Value Proposition].\n\nWould you be open to a brief conversation about how we could potentially help [Their Company]?\n\nBest regards,\n[Your Name]'
  }, {
    id: 2,
    name: 'Content Follow-up',
    category: 'follow-up',
    icon: <BookOpenIcon size={16} className="text-purple-600" data-id="element-645" />,
    description: 'Share relevant content based on their interests',
    template: "Hi [Name],\n\nYour comment on [Post Topic] caught my attention. Based on your interest in this area, I thought you might find this resource valuable: [Resource Title].\n\nI'd love to hear your thoughts on it, and I'm happy to share more insights if you're interested.\n\nBest,\n[Your Name]"
  }, {
    id: 3,
    name: 'Event Invitation',
    category: 'event',
    icon: <HeartIcon size={16} className="text-red-600" data-id="element-646" />,
    description: 'Invite them to an upcoming webinar or event',
    template: "Hi [Name],\n\nBased on your interest in [Topic], I wanted to personally invite you to our upcoming [Event Name] on [Date].\n\nWe'll be discussing [Event Topics], and I think you'd find it particularly valuable given your role at [Their Company].\n\nWould you like me to send you the registration details?\n\nBest regards,\n[Your Name]"
  }, {
    id: 4,
    name: 'Request for Feedback',
    category: 'value',
    icon: <ThumbsUpIcon size={16} className="text-green-600" data-id="element-647" />,
    description: 'Ask for their expert opinion on a topic',
    template: "Hi [Name],\n\nI've been following your insights on [Topic] and really value your perspective. We're currently working on [Project/Feature], and I'd love to get your thoughts on our approach.\n\nWould you be open to a quick 15-minute call next week to share your feedback? Your expertise would be incredibly helpful.\n\nBest,\n[Your Name]"
  }, {
    id: 5,
    name: 'Mutual Connection',
    category: 'introduction',
    icon: <HandshakeIcon size={16} className="text-orange-600" data-id="element-648" />,
    description: 'Reference a mutual connection or shared interest',
    template: "Hi [Name],\n\nI noticed we're both connected with [Mutual Connection], and I saw your comment about [Topic] on LinkedIn. Your perspective resonated with me, especially your point about [Specific Detail].\n\nI'd love to connect and potentially explore how [Your Company] and [Their Company] might collaborate on [Potential Area].\n\nBest regards,\n[Your Name]"
  }, {
    id: 6,
    name: 'Value Proposition',
    category: 'value',
    icon: <LightbulbIcon size={16} className="text-blue-600" data-id="element-649" />,
    description: 'Focus on the value you can provide to their business',
    template: "Hi [Name],\n\nI've been researching [Their Company] and noticed you might be facing challenges with [Common Industry Problem]. At [Your Company], we've helped similar businesses increase [Key Metric] by [Percentage].\n\nI'd love to share some insights specific to your situation. Would you be open to a brief conversation next week?\n\nBest regards,\n[Your Name]"
  }, {
    id: 7,
    name: 'Webinar Follow-up',
    category: 'follow-up',
    icon: <BookOpenIcon size={16} className="text-purple-600" data-id="element-650" />,
    description: 'Follow up after they attended your webinar',
    template: "Hi [Name],\n\nThank you for attending our [Webinar Name] last week. I hope you found the discussion on [Topic] valuable.\n\nBased on the questions you asked, I thought you might be interested in [Resource/Next Steps]. I'd be happy to discuss how these insights might apply specifically to [Their Company].\n\nWould you be available for a quick call this week?\n\nBest regards,\n[Your Name]"
  }];
  const filteredTemplates = templates.filter(template => {
    const matchesCategory = activeCategory === 'all' || template.category === activeCategory;
    const matchesSearch = searchTerm === '' || template.name.toLowerCase().includes(searchTerm.toLowerCase()) || template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-651">
      <h3 className="text-md font-medium text-gray-700 mb-2" data-id="element-652">
        Message Templates
      </h3>
      <p className="text-sm text-gray-500 mb-4" data-id="element-653">
        Choose a template as a starting point for your message
      </p>
      <div className="mb-4" data-id="element-654">
        <div className="relative" data-id="element-655">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-656">
            <SearchIcon size={16} className="text-gray-400" data-id="element-657" />
          </div>
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search templates..." className="block w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f]" data-id="element-658" />
        </div>
      </div>
      <div className="mb-4 overflow-x-auto" data-id="element-659">
        <div className="flex space-x-2 pb-2" data-id="element-660">
          {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-3 py-1 text-sm rounded-md whitespace-nowrap ${activeCategory === category.id ? 'bg-[#f9b30f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-661">
              {category.name}
            </button>)}
        </div>
      </div>
      <div className="space-y-3 max-h-[400px] overflow-y-auto" data-id="element-662">
        {filteredTemplates.length > 0 ? filteredTemplates.map(template => <div key={template.id} className="border border-gray-200 rounded-lg hover:border-gray-300 cursor-pointer transition-colors" data-id="element-663">
              <div className="p-3" onClick={() => onSelectTemplate(template.template)} data-id="element-664">
                <div className="flex justify-between items-center" data-id="element-665">
                  <div className="flex items-center" data-id="element-666">
                    <span className="mr-2" data-id="element-667">{template.icon}</span>
                    <h4 className="font-medium text-gray-800" data-id="element-668">
                      {template.name}
                    </h4>
                  </div>
                  <button onClick={e => {
              e.stopPropagation();
              onSelectTemplate(template.template);
            }} className="px-3 py-1 text-xs font-medium text-white bg-[#f9b30f] rounded hover:bg-[#e0a40e]" data-id="element-669">
                    Use
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-670">
                  {template.description}
                </p>
              </div>
            </div>) : <div className="text-center py-6" data-id="element-671">
            <p className="text-gray-500" data-id="element-672">No templates match your search.</p>
          </div>}
      </div>
    </div>;
};